import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { discordAPI } from "../../../api/apiClient";
import "../../../css/DiscordChannelDetails.css";
import "../../../css/DiscordChannelDetailsModern.css";
import useApi from "../../CustomHooks/useApi";
import { useToast } from "../../../Contexts/ToastContext";
import { FaPoll, FaDonate, FaKey } from "react-icons/fa";
import DiscordParticipantsList from "./DiscordPArticipantsList";
import DiscordUnpairedUsersList from "./DiscordUnapiredUsersList";
import DiscordLinksModal from "./DiscordLinksModal";
import Loading from "../../../Loading";
import { FaChevronDown } from "react-icons/fa"; 

const DiscordChannelDetails = () => {
  const { channelId, guid } = useParams();
  const { callApi } = useApi();
  const { showToast } = useToast();

  const [pairings, setPairings] = useState({
    paired: [],
    unpairedParticipants: [],
    unpairedDiscordUsers: [],
  });
  const [channelName, setChannelName] = useState("");
  const [discordUsers, setDiscordUsers] = useState([]);
  const [showLinksModal, setShowLinksModal] = useState(false);
  const [showUnpairedOnly, setShowUnpairedOnly] = useState(false);
  const [showHistoryPopup, setShowHistoryPopup] = useState(false);

  const [isRefreshing, setIsRefreshing] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  useEffect(() => {
    if (!channelId) return;
    setIsRefreshing(true);
    callApi(() => discordAPI.users.getAll(), (users) => {
      setDiscordUsers(users);
      callApi(() => discordAPI.access.getList(channelId), (data) => {
        setPairings(mergeUserInfo(data, users));
        if (data.channelName) setChannelName(data.channelName);
        setIsRefreshing(false);
        setIsInitialLoad(false);
      });
    });
  }, [channelId]);

  const mergeUserInfo = (pairings, discordUsers) => {
    if (!pairings?.paired || discordUsers.length === 0) return pairings;

    const updatedPaired = pairings.paired.map((p) => {
      if ((!p.userName || !p.displayName) && p.discordUserId) {
        const match = discordUsers.find((u) => u.id === p.discordUserId);
        if (match) {
          return {
            ...p,
            userName: match.username,
            displayName: match.displayName,
            avatarUrl: match.avatarUrl,
          };
        }
      }
      return p;
    });

    return { ...pairings, paired: updatedPaired };
  };

  const allParticipants = [
    ...pairings.paired.map((p) => ({
      participantId: p.participantId,
      participantName: p.participantName,
      discordUserId: p.discordUserId,
      discordDisplayName: p.displayName,
      status: p.status,
      isActive: p.isActive,
    })),
    ...pairings.unpairedParticipants.map((u) => ({
      participantId: u.participantId,
      participantName: u.participantName,
      discordUserId: null,
      discordDisplayName: null,
      status: u.status,
      isActive: u.status == 3 ? false : true,
    })),
  ].sort((a, b) =>
    (a.participantName || "").localeCompare(b.participantName || "", "pl", {
      sensitivity: "base",
    })
  );

  const handleSaveLinks = async (selected) => {
    if (!selected?.length) return;
    try {
      await discordAPI.access.autoLink(channelId, selected);
      showToast("Powiązania zostały zapisane.", "success");
    } catch (err) {
      console.error("Błąd przy zapisie powiązań:", err);
    } finally {
      setShowLinksModal(false);
      setIsRefreshing(true);

      callApi(() => discordAPI.access.getList(channelId), (data) => {
        setPairings(mergeUserInfo(data, discordUsers));
        if (data.channelName) setChannelName(data.channelName);
        setIsRefreshing(false);
      });
    }
  };

  const handleRevokeInactive = async () => {
    if (
      !window.confirm(
        "Czy na pewno chcesz odebrać dostęp wszystkim nieaktywnym uczestnikom?"
      )
    )
      return;

    setIsRefreshing(true);
    try {
      await callApi(() => discordAPI.access.revokeInactive(channelId));
    } catch (err) {
      console.error("Błąd przy odbieraniu dostępu:", err);
      showToast("Wystąpił błąd podczas odbierania dostępu ⚠️", "error");
    } finally {
      setShowLinksModal(false);
      showToast("Dostęp nieaktywnym osobom został odebrany.", "success");
      await callApi(() => discordAPI.access.getList(channelId), (data) => {
        setPairings(mergeUserInfo(data, discordUsers));
        if (data.channelName) setChannelName(data.channelName);
      });
      setIsRefreshing(false);
    }
  };

  const handleSendMessage = (type) => {
    let confirmText = "";
    if (type === 1)
      confirmText =
        "Czy na pewno chcesz wysłać wiadomość z informacją o ankiecie?";
    else if (type === 2)
      confirmText =
        "Czy na pewno chcesz wysłać wiadomość z informacją o funduszu?";
    else if (type === 3)
      confirmText =
        "Czy na pewno chcesz wysłać wiadomość z informacją o dostępie?";

    if (!window.confirm(confirmText)) return;
    callApi(() => discordAPI.channels.sendMessage(guid, channelId, type));
  };

   const handleRemoveDiscordUser = async (user) => {
    if (
      !window.confirm(
        `Czy na pewno chcesz usunąć użytkownika ${user.displayName || user.userName} z kanału Discord?`
      )
    )
      return;

    try {
      await callApi(() => discordAPI.access.removeUser(channelId, user.discordUserId));
      await callApi(() => discordAPI.access.getList(channelId), (data) => {
        setPairings(mergeUserInfo(data, discordUsers));
        if (data.channelName) setChannelName(data.channelName);
      });
    } catch (err) {
      showToast("Nie udało się usunąć użytkownika z kanału 😢", "error");
    }
  };


  return (
    <div className="dc-container">
      {isInitialLoad && isRefreshing ? (
        <Loading text="Ładowanie danych..." />
      ) : (
        <>
          <h2 className="dc-title">
            #{" "}<span className="dc-channel-name">{channelName}</span>
          </h2>

<div className="dc-automessages">
  <div className="dc-automessages-title">Automatyczne wiadomości:</div>

  <div className="dc-actions-bar">
    <button
      className="dc-action-btn dc-btn-survey"
      onClick={() => handleSendMessage(1)}
    >
      <FaPoll /> O ankiecie
    </button>
    <button
      className="dc-action-btn dc-btn-fund"
      onClick={() => handleSendMessage(2)}
    >
      <FaDonate /> O funduszu
    </button>
    <button
      className="dc-action-btn dc-btn-access"
      onClick={() => handleSendMessage(3)}
    >
      <FaKey /> O dostępie
    </button>
  </div>

<div
  className="dc-history-toggle"
  onClick={() => setShowHistoryPopup((prev) => !prev)}
>
  <FaChevronDown
    className={`dc-chevron ${showHistoryPopup ? "rotated" : ""}`}
  />
  {showHistoryPopup ? "Ukryj historię wiadomości" : "Pokaż historię wiadomości"}
</div>

  {showHistoryPopup && (
    <div className="dc-history-inline">
      {pairings.sentMessages?.length > 0 ? (
        <ul>
          {pairings.sentMessages.map((msg) => (
            <li key={msg.guid}>
              <span className="dc-history-date">
                {new Date(msg.sentAt).toLocaleString("pl-PL")}
              </span> <br />
              Wiadomość o{" "}
              <strong>
                {msg.messageType === "Survey"
                  ? "ankiecie"
                  : msg.messageType === "Fundraising"
                  ? "funduszu"
                  : msg.messageType === "MediaConsent"
                  ? "zgodach wizerunkowych"
                  : msg.messageType === "RoleAccess"
                  ? "dostępie"
                  : "-- nieznany typ wiadomości --"}
              </strong>{" "}
              została wysłana przez{" "}
              <b>{msg.sentBy}</b>
            </li>
          ))}
        </ul>
      ) : (
        <p className="dc-history-empty">Brak wysłanych wiadomości.</p>
      )}
    </div>
  )}
</div>




          {isRefreshing ? (
            <Loading text="Aktualizowanie danych..." />
          ) : (
            <div className="dc-columns">
              <DiscordParticipantsList
                combinedList={[
                  ...pairings.paired.map((p) => ({ ...p, isPaired: true })),
                  ...pairings.unpairedParticipants.map((u) => ({
                    participantName: u.participantName,
                    participantId: u.participantId,
                    isPaired: false,
                    status: u.status,
                  })),
                ]
                  .filter((p) => (showUnpairedOnly ? !p.isPaired : true))
                  .sort((a, b) =>
                    (a.participantName || "").localeCompare(
                      b.participantName || "",
                      "pl",
                      { sensitivity: "base" }
                    )
                  )}
                pairedCount={pairings.paired.length}
                showUnpairedOnly={showUnpairedOnly}
                onToggleShowUnpaired={() => setShowUnpairedOnly((p) => !p)}
                onOpenLinksModal={() => setShowLinksModal(true)}
              />

              <DiscordUnpairedUsersList
                users={[...pairings.unpairedDiscordUsers].sort((a, b) =>
                  (a.displayName || "").localeCompare(b.displayName || "", "pl", {
                    sensitivity: "base",
                  })
                )}
                onRemoveUser={handleRemoveDiscordUser}
              />
            </div>
          )}

          {showLinksModal && (
            <DiscordLinksModal
              isOpen={showLinksModal}
              onClose={() => setShowLinksModal(false)}
              participants={allParticipants}
              discordUsers={discordUsers}
              onSave={handleSaveLinks}
              onRevokeAbsent={handleRevokeInactive}
            />
          )}
        </>
      )}
    </div>
  );
};

export default DiscordChannelDetails;
